/**
 * Website analytics (Google Analytics 4): the connected properties' sessions, users, key events and revenue by date,
 * channel and source / medium, via /v1/connectors/ga4. Behind the server-evaluated `connectors.ga4` flag (default
 * off): off, unknown or failed -> the ordinary not-found screen (and no nav entry).
 */
import { useMemo } from 'react';
import { GA4_FLAG } from '@qm/contracts/connectors-ga4';
import { Heading, Loading, PageHeader } from '@qm/ui';
import { useWorkspaceName } from '../_shared/ListScreen';
import { useApp } from '../../../lib/app-context';
import { useFlag } from '../../../lib/flags';
import { createGa4Source } from '../../../ga4/logic';
import { Ga4View } from '../../../ga4/Ga4View';
import { NotFoundScreen } from '../../../shell/RouteStates';

export function AnalyticsPage() {
  const { t } = useApp();
  const state = useFlag(GA4_FLAG);
  if (state === 'off') return <NotFoundScreen />;
  if (state === 'loading') {
    return (
      <section className="qm-page" aria-labelledby="page-title">
        <Heading level={1} id="page-title">{t('connectors.ga4.title')}</Heading>
        <Loading label={t('common.loading')} />
      </section>
    );
  }
  return <Analytics />;
}

function Analytics() {
  const { t, locale } = useApp();
  const source = useMemo(() => createGa4Source({ kind: 'tenant' }), []);
  const workspace = useWorkspaceName();
  return (
    <section className="qm-page" aria-labelledby="page-title">
      <PageHeader eyebrow={workspace} title={t('connectors.ga4.title')} intro={t('connectors.ga4.intro')} />
      <Ga4View source={source} t={t} locale={locale} integrationsHref="/integrations" />
    </section>
  );
}
